import { VALET_FINDING_DOCS } from './valetParkingFindings.generated';
import type { HansAckerFinding, HansAckerReport } from './types';

const doc = (id: string) => VALET_FINDING_DOCS[id];

const findings: HansAckerFinding[] = [
  { id: 'vuln-0001', severity: 'high', type: 'code', folder: 'backend', location: 'backend/app/services/booking_service.py', cvss: 8.2, confidence: 'High', doc: doc('vuln-0001') },
  { id: 'vuln-0003', severity: 'medium', type: 'code', folder: 'backend', location: 'backend/app/api/v1/endpoints/orders.py', cvss: 6.5, confidence: 'Medium', doc: doc('vuln-0003') },
  { id: 'vuln-0002', severity: 'medium', type: 'code', folder: 'backend', location: 'backend/app/api/v1/endpoints/team.py', cvss: 5.3, confidence: 'Medium', doc: doc('vuln-0002') },
];

export const valetParkingReport: HansAckerReport = {
  projectLabel: 'valet-parking',
  verdict: { label: 'Overall security posture', headline: 'Elevated risk — missing authorization boundary' },
  businessRisk:
    'Unauthenticated callers can mutate bookings and access or cancel orders because API routes lack authentication and hardcode a user identity. A race in booking creation can additionally bypass duplicate and capacity checks, allowing duplicate reservations and capacity over-issuance.',
  scanDetails:
    'White-box assessment aligned with OWASP WSTG, PTES and source-aware SAST practices. Scope: FastAPI backend, React frontend, ORM services, outbound FEPLAS integration, configuration and dependency surfaces. Activities included repository and route mapping, threat modelling, Semgrep and AST-grep, secret scanning, filesystem triage, source-to-sink review, and focused authorization/IDOR, race-condition, injection, XSS, CSRF, SSRF, XXE, upload and business-logic analysis. The local runtime could not be fully exercised because required FastAPI/database services were unavailable, so findings carry static-analysis confidence.',
  meta: { scanned: '22 Oct 2026, 05:25 UTC', duration: '6 min', mode: 'Quick', surfaces: 25 },
  severitySummary: {
    critical: 'No critical findings were identified in this scan.',
    high: 'Concurrent booking creation bypasses duplicate-booking and capacity checks through a non-atomic read-then-insert.',
    medium: 'Unauthenticated team and order routes allow arbitrary status and comment changes, plus order access and cancellation via a hardcoded user ID.',
    info: 'No informational findings were filed in this scan.',
  },
  fixFirst: [
    { phase: 'IMMEDIATE', title: 'Make booking creation atomic', description: 'Wrap duplicate and capacity checks in a transaction with row/advisory locking and enforce uniqueness at the database boundary in `BookingService.create_booking`.', findingId: 'vuln-0001' },
    { phase: 'IMMEDIATE', title: 'Require authentication and team roles on team routes', description: '`/api/v1/team/*` endpoints accept unauthenticated calls with a hardcoded actor. Derive the principal from a verified token.', findingId: 'vuln-0002' },
    { phase: 'IMMEDIATE', title: 'Enforce the authenticated user on order operations', description: 'Order list, detail and cancel routes force `current_user_id = 1`. Replace it with the verified principal and ownership checks.', findingId: 'vuln-0003' },
    { phase: 'SHORT-TERM', title: 'Add authorization and concurrency regression tests', description: 'Cover 401/403 for anonymous calls, cross-user order access and parallel booking creation.', findingId: 'vuln-0002' },
  ],
  findings,
  coverage: {
    surfacesReviewed: 25,
    riskAreas: 10,
    chips: [
      { label: '12 surfaces reported \u2192 3 findings', tone: 'found' },
      { label: '9 no issue found', tone: 'ok' },
      { label: '2 ruled out', tone: 'muted' },
      { label: '1 not applicable', tone: 'muted' },
      { label: '1 needs follow-up', tone: 'warn' },
    ],
    items: [
      { status: 'found', name: 'Race conditions / concurrency', detail: 'Non-atomic duplicate and capacity checks in booking creation \u2014 reported (vuln-0001)' },
      { status: 'found', name: 'Authentication and credentials', detail: 'Team and order routes have no auth dependency (vuln-0002, vuln-0003)' },
      { status: 'found', name: 'IDOR / object-level authorization', detail: 'Orders enumerated and cancelled as hardcoded user 1 (vuln-0003)' },
      { status: 'found', name: 'Broken function-level authorization', detail: 'Team status and key-hook mutations lack team-role checks (vuln-0002)' },
      { status: 'clean', name: 'SQL / NoSQL injection', detail: 'ORM queries use bound SQLAlchemy expressions' },
      { status: 'clean', name: 'XSS', detail: 'React output uses JSX interpolation; no HTML sinks identified' },
      { status: 'clean', name: 'SSRF', detail: 'Ruled out \u2014 vehicle lookup outbound URL is not attacker-controlled' },
      { status: 'clean', name: 'File upload / XML / archive', detail: 'No XML parsing, upload or archive extraction found' },
      { status: 'na', name: 'CSRF', detail: 'Not applicable \u2014 bearer-header/localStorage auth, no ambient cookies' },
      { status: 'clean', name: 'Admin state-changing routes', detail: 'Admin handlers are TODO stubs returning constants; no state impact found' },
    ],
    followUp: [
      'FeplasClient outbound HTTP calls: TLS certificate verification could not be confirmed \u2014 restore verification and validate the configured trust chain.',
      'Runtime validation was unavailable; re-run the authorization and concurrency PoCs against a provisioned database and backend services.',
    ],
  },
  recommendations: [
    {
      id: 'immediate',
      title: 'Immediate',
      items: [
        'Require a single authentication dependency on all non-public API routes and derive the principal only from a verified token or session.',
        'Remove every hardcoded `current_user_id = 1` and enforce role and object-level authorization on team, order and booking operations.',
        'Make booking creation atomic with transactions, row/advisory locking and database-enforced uniqueness and capacity.',
      ],
    },
    {
      id: 'short',
      title: 'Short-term',
      items: [
        'Add regression tests proving unauthenticated requests receive 401/403 and cross-user order and team mutations are rejected.',
        'Add concurrent booking tests proving duplicate and capacity invariants under parallel requests.',
        'Restore TLS certificate verification in the FEPLAS client and validate the configured trust chain.',
        'Rotate any populated credentials in checked-in environment configuration and move secrets to a managed store.',
      ],
    },
    {
      id: 'medium',
      title: 'Medium-term',
      items: [
        'Provision a test database and backend dependencies, then re-run the authorization and concurrency PoCs end to end.',
        'Centralize deny-by-default authorization middleware and consistent input validation, and add security checks to CI.',
      ],
    },
  ],
  scans: [
    { id: 'valet-parking-project_58c7', scannedAt: '22 Oct 2026, 05:25 UTC', duration: '6 min', mode: 'Quick', findings: 3, latest: true, downloadUrl: '/hans-acker/scans/valet-parking-project_58c7.zip' },
    { id: 'valet-parking-project_ae10', scannedAt: '16 Oct 2026, 09:11 UTC', duration: '20 min', mode: 'Quick', findings: 7, downloadUrl: '/hans-acker/scans/valet-parking-project_ae10.zip' },
  ],
  downloads: {
    zip: '/hans-acker/scans/valet-parking-project_58c7.zip',
  },
};
